import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldAlert, Loader2, Copy } from "lucide-react";

export default function Security() {
  const { user, refreshUser } = useAuth();
  const [setup, setSetup] = useState(null); // { qr, secret }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");
  const enabled = !!user?.mfa_enabled;

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetup(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code });
      setBackupCodes(data.backup_codes);
      setSetup(null);
      setCode("");
      await refreshUser();
      toast.success("Two-factor authentication enabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: disableCode });
      setDisableCode("");
      await refreshUser();
      toast.success("Two-factor authentication disabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Account security</p>
        <h1 className="font-display text-4xl font-light mt-1">Security</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Protect your account with two-factor authentication (2FA) using an authenticator app.
        </p>
      </div>

      <div className="border border-zinc-200 bg-white p-8" data-testid="mfa-card">
        <div className="flex items-center gap-3 mb-6">
          <div className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${enabled ? "bg-[#156F52]/10 text-[#156F52]" : "bg-zinc-100 text-zinc-500"}`}>
            {enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
          </div>
          <div>
            <h2 className="font-display text-2xl">Two-factor authentication</h2>
            <p className="text-sm text-zinc-500" data-testid="mfa-status">
              Status: <span className={`font-medium ${enabled ? "text-[#156F52]" : "text-[#B54747]"}`}>{enabled ? "Enabled" : "Not enabled"}</span>
            </p>
          </div>
        </div>

        {backupCodes && (
          <div className="mb-6 border border-[#D5A44A] bg-[#FBF7E9] p-5" data-testid="mfa-backup-codes">
            <p className="font-medium text-[#6B5410]">Save your backup codes</p>
            <p className="text-sm text-[#6B5410]/80 mt-1 mb-3">Each code works once if you lose your authenticator. Store them somewhere safe — they won't be shown again.</p>
            <div className="grid grid-cols-2 gap-2 font-mono-data text-sm">
              {backupCodes.map((c) => <span key={c} className="bg-white border border-[#D5A44A]/40 px-2 py-1 text-center">{c}</span>)}
            </div>
            <button
              onClick={() => { navigator.clipboard?.writeText(backupCodes.join("\n")); toast.success("Backup codes copied"); }}
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#6B5410] hover:underline"
              data-testid="copy-backup-codes"
            >
              <Copy className="h-3.5 w-3.5" /> Copy all
            </button>
          </div>
        )}

        {!enabled && !setup && (
          <Button onClick={startSetup} disabled={busy} data-testid="mfa-setup-button" className="bg-[#0B1F33] text-white rounded-none h-11">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Set up 2FA
          </Button>
        )}

        {!enabled && setup && (
          <div data-testid="mfa-setup-panel">
            <p className="text-sm text-zinc-600 mb-3">1. Scan this QR code with Google Authenticator, Authy or 1Password.</p>
            <img src={setup.qr} alt="2FA QR code" className="h-44 w-44 border border-zinc-200" data-testid="mfa-qr" />
            <p className="text-xs text-zinc-500 mt-2">Can't scan? Enter this key manually: <code className="font-mono-data">{setup.secret}</code></p>
            <p className="text-sm text-zinc-600 mt-5 mb-2">2. Enter the 6-digit code to confirm.</p>
            <div className="flex gap-2 max-w-xs">
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="mfa-enable-code" className="rounded-none h-11 tracking-[0.3em] text-center" />
              <Button onClick={enable} disabled={busy || code.length < 6} data-testid="mfa-enable-button" className="bg-[#0B1F33] text-white rounded-none h-11 shrink-0">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enable
              </Button>
            </div>
          </div>
        )}

        {enabled && (
          <div data-testid="mfa-disable-panel">
            <Label className="text-zinc-500 text-xs uppercase tracking-widest font-mono-data">Enter a code to disable 2FA</Label>
            <div className="flex gap-2 max-w-xs mt-2">
              <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="123456 or backup code" data-testid="mfa-disable-code" className="rounded-none h-11" />
              <Button onClick={disable} disabled={busy || !disableCode} variant="outline" data-testid="mfa-disable-button" className="rounded-none h-11 border-[#B54747] text-[#B54747] hover:bg-[#B54747]/5 shrink-0">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Disable
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
